import { useAppSelector } from "@app/store";
import Warning from "@assets/icons/warning-filled.svg";
import { Button, MiniButton } from "@components/button";
import { errorMessage } from "@components/forms/error";
import { sanitizeValue } from "@components/forms/utils";
import { Input } from "@components/input";
import { FlexLoader } from "@components/loader";
import { Modal } from "@components/modal";
import { useBalanceDispatch } from "@features/balance";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import styles from "./transfer-money-modal.module.scss";

type Props = {
  hide: () => void;
  type?: "payOut" | "payIn";
};

export const TransferMoneyModal = ({ hide, type }: Props) => {
  const [currentModal, setCurrentModal] = useState<"initial" | "transferMoney">(
    "initial"
  );

  const onConfirmInitialModal = () => {
    setCurrentModal("transferMoney");
  };

  const onCloseModal = () => {
    hide();
    setCurrentModal("initial");
  };

  if (!type) {
    return null;
  }

  return (
    <>
      {currentModal === "initial" && (
        <InitialModal
          onConfirm={onConfirmInitialModal}
          onCancel={onCloseModal}
        />
      )}

      {currentModal === "transferMoney" && (
        <TransferModal type={type} hide={onCloseModal} />
      )}
    </>
  );
};

type InitialModalProps = {
  onConfirm: () => void;
  onCancel: () => void;
};

const InitialModal = ({ onCancel, onConfirm }: InitialModalProps) => {
  return (
    <Modal isOpen={true} onClose={onCancel} className={styles.payoutsConfirm}>
      <div className={styles.payoutsConfirmContainer}>
        <Warning />
        <p className={styles.title}>Вы уверены?</p>
        <p className={styles.subTitle}>Перевод прибыли на трастовый баланс</p>
      </div>
      <div className={styles.buttons}>
        <Button onClick={onConfirm}>Да, перевести</Button>
        <Button type="danger" onClick={onCancel}>
          Нет
        </Button>
      </div>
    </Modal>
  );
};

type FormData = {
  amount: number;
};

const TransferModal = ({ hide, type }: Required<Props>) => {
  const { transferMoney } = useBalanceDispatch();
  const payIn = useAppSelector((state) => state.balance.payIn);
  const payOut = useAppSelector((state) => state.balance.payOut);

  const schema = useMemo(
    () =>
      yup.object().shape({
        amount: yup
          .number()
          .transform(sanitizeValue)
          .required(errorMessage.required)
          .positive(errorMessage.onlyPositive)
          .max(
            type === "payIn" ? (payIn ?? 0) : (payOut ?? 0),
            errorMessage.maxSum
          ),
      }),
    [payIn, payOut, type]
  );

  const {
    control,
    handleSubmit,
    setError,
    formState: { isSubmitting },
    setValue
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const onConfirm = useCallback(
    async (data: FormData) => {
      try {
        await transferMoney({
          operation: type === "payIn" ? "pay_in" : "pay_out",
          amount: data.amount,
        });

        hide();
      } catch (error) {
        setError("amount", {
          type: "server",
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          message: error.message,
        });
      }
    },
    [hide, setError, transferMoney, type]
  );

  const setMax = () => {
    setValue("amount", type === "payIn" ? (payIn ?? 0) : (payOut ?? 0))
  }

  return (
    <Modal
      className={styles.transferModal}
      isOpen={true}
      onClose={hide}
      title="Вывод баланса"
    >
      {isSubmitting ? (
        <FlexLoader />
      ) : (
        <>
          <div className={styles.wrapper}>
            <Input
              label="Сумма"
              type="number"
              name="amount"
              control={control}
              placeholder="Введите сумму"
              className={styles.input}
              postNote="USDT"
            />
            <MiniButton onClick={setMax}>Max</MiniButton>
          </div>
          <Button className={styles.button} onClick={handleSubmit(onConfirm)}>
            Готово
          </Button>
        </>
      )}
    </Modal>
  );
};
